import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { isEventOver } from './utils';
import {
    getWsInterface,
    WS_CONNECTION_NAME,
    UNNAMED_WS_HANDLER_SCOPE,
} from '../../spa/Utils/Websockets';

const WS_INPLAY_UPDATE_ACTION = 'inplay_update';
const WS_PRICE_UPDATE_ACTION = 'price_update';

const wsInterface = getWsInterface();

/**
 * Sets if the handlers have been set or not
 * @type {Boolean}
 */
let isInplayEventHandlersSet = false;

/**
 * A Set holding the list of inplay update event listeners
 * @type {Set}
 */
const inplayEventUpdateListeners = new Set();

/**
 * The inplay event update websocket handler
 * @param  {Array} args The arguments passed from the websocket
 * @return {void}
 */
const inplayEventUpdateHandler = (...args) => {
    const listeners = Array.from(inplayEventUpdateListeners);

    listeners.forEach(listener => listener(...args));
};

/**
 * A helper to set the websocket handlers
 * @param {String} [wsHandlerScope=UNNAMED_WS_HANDLER_SCOPE] a unique scope for the handler e.g. "RacingWidget-horse-racing"
 * @return {void}
 */
const setInplayEventHandlers = (wsHandlerScope = UNNAMED_WS_HANDLER_SCOPE) => {
    wsInterface.requestHandler(WS_CONNECTION_NAME).registerHandler(WS_INPLAY_UPDATE_ACTION, inplayEventUpdateHandler, wsHandlerScope);
    wsInterface.requestHandler(WS_CONNECTION_NAME).registerHandler(WS_PRICE_UPDATE_ACTION, inplayEventUpdateHandler, wsHandlerScope);

    isInplayEventHandlersSet = true;
};

/**
 * Format to subscription/unsubscription structure
 *
 * @param {Array} events structure: [{event_id: id, markets: [mId,...]}, ...]
 * @return {Array} structure [{event_id: id, market_id: mId }, ...]
 * (one element per eventId, marketId combination)
 */
const formatToSubscribe = events => events.reduce((eventsAcc, currEvent) => {
    const { event_id: eventId, markets } = currEvent;
    const elementsToAdd = markets.length ?
        markets.map(marketId => ({
            event_id: eventId,
            market_id: marketId,
        })) : [{
            event_id: eventId,
        }];

    return [
        ...eventsAcc,
        ...elementsToAdd,
    ];
}, []);

/**
 * Starts listening for inplay event updates
 * @param  {Array} events An array of events and market objects [{event_id: id, markets: [id]}]
 * @param {String} [wsHandlerScope=UNNAMED_WS_HANDLER_SCOPE] a unique scope for the handler e.g. "RacingWidget-horse-racing"
 * @return {void}
 */
export function startListeningForInplayEventUpdates(events, wsHandlerScope = UNNAMED_WS_HANDLER_SCOPE) {
    const eventsToSubscribe = formatToSubscribe(events);
    wsInterface.requestHandler(WS_CONNECTION_NAME).subscribe(eventsToSubscribe);

    if (!isInplayEventHandlersSet) {
        wsInterface.onCloseConnections(() => {
            isInplayEventHandlersSet = false;
        });

        setInplayEventHandlers(wsHandlerScope);
    }
}

/**
 * Subscribes to event updates
 * @param  {Function} callback A callback that is called with the event data
 * @return {Function} A callback to unsubscribe from the event
 */
export function onInplayEventUpdate(callback) {
    inplayEventUpdateListeners.add(callback);

    return function unsubscribe() {
        inplayEventUpdateListeners.delete(callback);
    };
}

/**
 * Handles inplay selection updates
 * @param {Object} selectionData The selection data
 * @param {Function} callback The function to call back with the selection update
 * @return {Function} A callback to unsubscribe from the selection update
 */
export function onInplaySelectionUpdate({ selectionId, marketId, eventId }, callback) {

    return onInplayEventUpdate(({ event = {} } = {}) => {
        if (String(event.id) === String(eventId)) {
            const eventSuspended = event.suspended;
            const markets = event.markets || [];
            const market = markets.find(({ id }) => String(id) === String(marketId));

            if (isUndefined(market) || isEmpty(market.selections)) {
                callback({ suspended: eventSuspended });
            } else {
                const selections = market.selections;
                const marketSuspended = market.suspended;

                if (!isUndefined(selections[selectionId])) {
                    const suspended = eventSuspended || marketSuspended || selections[selectionId].suspended;
                    callback({ ...selections[selectionId], id: selectionId, suspended });
                }
            }
        }
    });
}

/**
 * Handle on inplay event over by event id
 * @param  {String|Number} id The event id to listen for
 * @param  {Function} callback A callback that is called when the event is over
 * @return {Function} A callback to unsubscribe from the event
 */
export function onInplayEventOverById(id, callback) {
    return onInplayEventUpdate(({ event = {} } = {}) => {
        if (String(event.id) === String(id) && isEventOver(event.match_status)) {
            callback({event});
        }
    });
}

